import React, { useEffect, useState, useCallback } from "react";
import { Badge, Button, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { startOfDay, endOfDay, addDays } from "date-fns";
import Cookies from "universal-cookie";
import { cookiesKeyNames, abbreviationToPublisherIdMap } from "@revbox/common-fe";
import { generateCompatibleColors } from "../helpers/generate-compatible-colors";

import InventoryInsightsRevenue from "./analytics/inventory/insights/InventoryInsightsRevenue";
import InventoryInsightsTotals from "./analytics/inventory/insights/InventoryInsightsTotals";
import InventoryInsightsCampaignsAndPlaceholders from "./analytics/inventory/insights/InventoryInsightsCampaignsAndPlaceholders";
import InventoryInsightsMap from "./analytics/inventory/insights/InventoryInsightsMap";
import { getPublisherInventory } from "../services/pub";
import DatePicker from "./components/DatePicker";
import SpecificInventoryPositionsChart from "./analytics/inventory/insights/SpecificInventoryPositionsChart";
import SpecificInventoryClickDistributionChart from "./analytics/inventory/insights/SpecificInventoryClickDistributionChart";
import { primaryColorModule } from "../modules/primaryColor";
import { useCookiesContext, isCookiesEnabled} from '../contexts/CookiesContext'
import { safeParse } from "@helpers/json-helper";


const cookies = new Cookies();

const Ranges = [
  {
    label: "today",
    value: [startOfDay(new Date()), endOfDay(new Date())],
  },
  {
    label: "yesterday",
    value: [
      startOfDay(addDays(new Date(), -1)),
      endOfDay(addDays(new Date(), -1)),
    ],
  },
  {
    label: "Last 30 Days",
    value: [startOfDay(addDays(new Date(), -29)), endOfDay(new Date())],
  },
];

function getPublisherId() {
  const searchParams = new URLSearchParams(window.location.search);
  const urlDomain = window.location.hostname;
  const pubParam = searchParams.get("pub");
  const publisherIdFromCookies = isCookiesEnabled
    ? cookies.get(cookiesKeyNames.publisherId)
    : localStorage.getItem(cookiesKeyNames.publisherId);

  if (pubParam) {
    if (abbreviationToPublisherIdMap[pubParam]) {
      return abbreviationToPublisherIdMap[pubParam];
    } else {
      return pubParam;
    }
  }

  if (urlDomain === "ppc.hoteltechreport.com") {
    return 67;
  }

  if (publisherIdFromCookies) {
    return Number(publisherIdFromCookies);
  }

  return 1;
}

function renderInventoryType(inventoryType) {
  let inventoryTypeText = "";

  if (inventoryType === 1) {
    inventoryTypeText = "DEPARTMENT";
  } else if (inventoryType === 2) {
    inventoryTypeText = "SPECIFIC";
  } else if (inventoryType === 3) {
    inventoryTypeText = "HOMEPAGE";
  } else if (inventoryType === 4) {
    inventoryTypeText = "BANNER";
  } else if (inventoryType === 5) {
    inventoryTypeText = "CLUSTER";
  } else if (inventoryType === 34) {
    inventoryTypeText = "SPOTLIGHT";
  }

  return (
    <Badge
      bg="outline-secondary"
      className="me-1 text-secondary"
      style={{
        width: "max-content",
        maxWidth: "180px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        pointerEvents: "none",
        marginLeft: "10px",
        verticalAlign: "middle",
      }}
    >
      {inventoryTypeText}
    </Badge>
  );
}

export default function PublisherInventoryInsights() {
  // get 30 days back for begin date
  const [currentPeriod, setCurrentPeriod] = useState([null, null]);
  // get 60 days back for begin date
  const [previousPeriod, setPreviousPeriod] = useState([null, null]);

  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const { cookiesData, updateCookiesProperty, getPathKey, getSkinMode } =
    useCookiesContext();

  const publisher_id = getPublisherId();

  const loadDateRange = useCallback(() => {
    const storedCurrentPeriod = isCookiesEnabled
      ? cookiesData[getPathKey()]?.[cookiesKeyNames.currentPeriod]
      : safeParse(cookiesData[getPathKey()]?.[cookiesKeyNames.currentPeriod]);
    const storedPreviousPeriod = isCookiesEnabled
      ? cookiesData[getPathKey()]?.[cookiesKeyNames.previousPeriod]
      : safeParse(cookiesData[getPathKey()]?.[cookiesKeyNames.previousPeriod]);

    if (storedCurrentPeriod && storedPreviousPeriod) {
      return {
        currentPeriod: storedCurrentPeriod.map((date) => new Date(date)),
        previousPeriod: storedPreviousPeriod.map((date) => new Date(date)),
      };
    }

    const begin_of_day = startOfDay(new Date());
    const end_of_day = endOfDay(new Date());

    // Fallback to the last 30 days if nothing is stored in cookies
    const defaultCurrentPeriod = [
      addDays(begin_of_day, -29),
      end_of_day,
    ];
  
    const defaultPreviousPeriod = [
      addDays(begin_of_day, -59),
      addDays(end_of_day, -29),
    ];

    return {
      currentPeriod: defaultCurrentPeriod,
      previousPeriod: defaultPreviousPeriod,
    };
  }, [cookiesData, getPathKey]);

  useEffect(() => {
    const { currentPeriod, previousPeriod } = loadDateRange();
    setCurrentPeriod(currentPeriod);
    setPreviousPeriod(previousPeriod);
  }, []);

  useEffect(() => {
    if (
      currentPeriod &&
      currentPeriod.length === 2 &&
      currentPeriod[0] &&
      currentPeriod[1]
    ) {
      updateCookiesProperty(cookiesKeyNames.currentPeriod, currentPeriod);
      updateCookiesProperty(cookiesKeyNames.previousPeriod, previousPeriod);
    }
  }, [currentPeriod, previousPeriod]);

  const [response, setResponse] = useState();
  const [editLink, setEditLink] = useState();
  const inventoryId = new URLSearchParams(window.location.search).get("id");

  useEffect(() => {
    if (!inventoryId) {
      return;
    }

    getPublisherInventory(inventoryId).then((res) => {
      if (!res?.data) {
        return;
      }

      const columns = Object.keys(res.data);
      const values = columns.map((column) => {
        return {
          value: res.data[column],
          column: column,
        };
      });

      const newEditLink =
        "/pub/inventory/edit?" +
        values.map((value) => `${value.column}=${value.value}`).join("&");
      setEditLink(newEditLink);
      setResponse(res);
    });
  }, [inventoryId]);

  const formatDate = useCallback((date) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("en-UK", options).format(date);
  }, []);

  const handleOpenDateRangePicker = () => {
    setIsDateRangePickerOpen(true);
  };

  const handleCloseDateRangePicker = () => {
    setIsDateRangePickerOpen(false);
  };

  const handleDateRange = useCallback((e) => {
    const curBegin = new Date(e[0]);
    const curEnd = new Date(e[1]);

    curBegin.setHours(0, 0, 0, 0);
    curEnd.setHours(23, 59, 59, 999);

    const diff = curEnd.getTime() - curBegin.getTime() + 1;

    const prevBegin = new Date(curBegin.getTime() - diff);
    const prevEnd = new Date(curEnd.getTime() - diff);

    setPreviousPeriod([prevBegin, prevEnd]);
    setCurrentPeriod([curBegin, curEnd]);
  }, []);

  const currentSkin = getSkinMode();


  const colors = generateCompatibleColors(primaryColorModule(publisher_id), 10);

  return (
    <React.Fragment>
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="#">PUB Home</Link>
              </li>
              <li className="breadcrumb-item">Dashboard</li>
              <li className="breadcrumb-item">Inventory</li>
              <li className="breadcrumb-item active" aria-current="page">
                ID {inventoryId}
              </li>
            </ol>
            <h2 className="main-title mb-0">
              {response?.data?.name}
              {renderInventoryType(response?.data?.inventory_type)}
            </h2>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Link to={editLink}>
              <Button
                variant=""
                className="d-flex align-items-center gap-2 btn btn-outline-primary"
              >
                Edit Inventory
              </Button>
            </Link>
            <Button
              variant="btn-outline-primary"
              className={`d-flex align-items-center gap-2 btn btn-outline-primary`}
              onClick={handleOpenDateRangePicker}
            >
              <span className="fw-semibold">Date Range</span>
              <i className="ri-arrow-right-s-line"></i>
              {currentPeriod && currentPeriod.length === 2
                ? ` ${formatDate(currentPeriod[0])} - ${formatDate(
                    currentPeriod[1]
                  )}`
                : ""}
            </Button>
            {isDateRangePickerOpen && (
              <DatePicker
                isDateRangePickerOpen={isDateRangePickerOpen}
                setIsDateRangePickerOpen={setIsDateRangePickerOpen}
                ranges={Ranges}
                onChange={handleDateRange}
                currentPeriod={currentPeriod}
              />
            )}
          </div>
        </div>

        {inventoryId && currentPeriod && (
          <Row className="g-3 justify-content-center">
            <InventoryInsightsRevenue
              currentPeriod={currentPeriod}
              previousPeriod={previousPeriod}
              inventoryId={inventoryId}
            />
            <InventoryInsightsTotals
              currentPeriod={currentPeriod}
              previousPeriod={previousPeriod}
              inventoryId={inventoryId}
            />
            <InventoryInsightsCampaignsAndPlaceholders
              currentPeriod={currentPeriod}
              previousPeriod={previousPeriod}
              inventoryId={inventoryId}
              inventory={response?.data}
              skin={currentSkin}
            />
            <Row className="g-3 p-0" style={{ marginTop: "5px" }}>
              <SpecificInventoryPositionsChart
                currentPeriod={currentPeriod}
                inventoryId={inventoryId}
                colors={colors}
              />
              <SpecificInventoryClickDistributionChart
                currentPeriod={currentPeriod}
                inventoryId={inventoryId}
                colors={colors}
              />
            </Row>
            <InventoryInsightsMap
              currentPeriod={currentPeriod}
              previousPeriod={previousPeriod}
              inventoryId={inventoryId}
            />
          </Row>
        )}

        
      </div>
    </React.Fragment>
  );
}
